// Picker sheet — the option list for `select` fields. Plain list up to the
// search threshold; past it, a live-filtering search row (auto-focused) with an
// explicit empty state. Esc and the backdrop both close without choosing.

import { useEffect, useMemo, useRef, useState } from 'react';
import styles from './ConversationalForms.module.css';
import { CloseIcon, SearchIcon } from './icons';
import { CfField } from './types';
import { useEscLayer, useFocusTrap } from './useFocusTrap';

/** Option count past which the picker gains its search row. */
export const PICKER_SEARCH_THRESHOLD = 8;

type PickerSheetProps = {
    field: CfField;
    value: string;
    onPick: (value: string) => void;
    onClose: () => void;
};

const PickerSheet = ({ field, value, onPick, onClose }: PickerSheetProps) => {
    const [query, setQuery] = useState('');
    const searchable = (field.options?.length ?? 0) > PICKER_SEARCH_THRESHOLD;
    const searchRef = useRef<HTMLInputElement>(null);

    // Topmost layer while open: Esc closes only the picker, and the trap
    // wraps Tab within the sheet and hands focus back to the invoking select
    // on close. Declared before the search autofocus below so the trap
    // records the *select* as the control to restore — and its deferred
    // initial claim then yields to the autofocused search row.
    useEscLayer(onClose);
    const trapRef = useFocusTrap<HTMLDivElement>();

    useEffect(() => {
        if (searchable) searchRef.current?.focus();
    }, [searchable]);

    const options = useMemo(() => {
        const all = field.options ?? [];
        const q = query.trim().toLowerCase();
        if (!q) return all;
        return all.filter((o) => o.label.toLowerCase().includes(q));
    }, [field.options, query]);

    return (
        <div className={styles.sheetBackdrop} onClick={onClose} data-testid="cf-picker">
            <div
                ref={trapRef}
                className={styles.sheet}
                role="dialog"
                aria-modal="true"
                aria-label={field.label}
                onClick={(e) => e.stopPropagation()}
            >
                <div className={styles.sheetHandle} aria-hidden="true" />
                <div className={styles.sheetHeader}>
                    <span className={styles.sheetTitle}>{field.label}</span>
                    <button type="button" className={styles.sheetClose} onClick={onClose} aria-label="Close">
                        <CloseIcon />
                    </button>
                </div>
                {searchable && (
                    <div className={styles.sheetSearch}>
                        <SearchIcon />
                        <input
                            ref={searchRef}
                            className={styles.sheetSearchInput}
                            type="text"
                            placeholder={`Search ${field.options?.length ?? 0} options…`}
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            aria-label={`Search ${field.label} options`}
                        />
                    </div>
                )}
                <div className={styles.sheetList}>
                    {options.map((option) => (
                        <button
                            type="button"
                            key={option.value}
                            className={`${styles.sheetOption} ${option.value === value ? styles.sheetOptionSelected : ''}`}
                            onClick={() => onPick(option.value)}
                        >
                            {option.label}
                            {option.value === value && (
                                <span className={styles.sheetOptionCheck} aria-hidden="true">
                                    ✓
                                </span>
                            )}
                        </button>
                    ))}
                    {!options.length && (
                        <div className={styles.sheetEmpty}>No matches for “{query.trim()}”.</div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PickerSheet;
